import { Pill } from '@/components/ui/pill';
import { LOGO_URL } from '@/lib/asset';

/**
 * The two pages a person sees before they have a session: signing in, and choosing a
 * password from an invitation. They share this frame so arriving from an invitation looks
 * like arriving at the product rather than at a form somebody forgot to style - the second
 * of the two used to be a bare box in the middle of a white page.
 */
export function AuthLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="theme-light grid min-h-screen w-full bg-surface text-foreground lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
      <ShowcasePanel />
      <main className="flex min-w-0 items-center justify-center px-6 py-12 sm:px-10">
        <div className="anim-rise flex w-full max-w-[400px] flex-col gap-6">{children}</div>
      </main>
    </div>
  );
}

/**
 * The left half shows the product instead of describing it: an e-file summary and its
 * transmissions, built from the same components the app uses, over the brand light blue.
 * The figures are the shape of what a customer sees after e-file, not data.
 */
export function ShowcasePanel() {
  const transmissions = [
    ['1', '2025-88558455937-c66b3669c', 'Accepted'],
    ['2', '2025-88558515922-9390fd7c4', 'Accepted'],
    ['3', '2025-88558573664-82cd8433e', 'Processing'],
  ];
  const stats = [
    ['Records', '2,400', '#0f1e38'],
    ['Accepted', '2,377', '#1b6b3a'],
    ['With errors', '23', '#8a5a00'],
  ];
  const points = [
    '16 form types, 1099-NEC to 1042-S',
    'IRS test system before live e-file',
    'Corrections and replacements handled',
  ];

  return (
    <aside className="relative hidden flex-col justify-between overflow-hidden bg-gradient-to-br from-[#eef5fd] via-[#dcecf5] to-[#d5e9ef] px-12 py-12 lg:flex xl:px-16">
      <img src={LOGO_URL} alt="TAB Service Company" width={190} height={58} className="h-14 w-auto self-start" />

      <div className="relative my-10">
        <h2 className="max-w-[22ch] text-[30px] leading-[1.2] font-semibold text-[#0f1e38]">
          File information returns with the IRS through Tab Service.
        </h2>
        <p className="mt-4 max-w-[48ch] text-[15px] leading-6 text-[#3b4a63]">
          Send CSV, Excel, IRS 1220 files or IRIS XML. Every record is validated, transmitted under the
          Tab Service TCC, and reported back with its IRS result.
        </p>

        <div className="relative mt-10 h-[300px] max-w-[560px]">
          <div className="absolute top-0 right-0 w-[380px] rounded-xl border border-[#e3edf7] bg-white p-4 shadow-[0_4px_24px_rgba(35,79,146,0.10)]">
            <p className="text-[11px] font-semibold tracking-[0.08em] text-[#64748b] uppercase">Transmissions</p>
            <table className="mt-2 w-full text-[13px]">
              <tbody>
                {transmissions.map(([chunk, receipt, status]) => (
                  <tr key={chunk} className="border-t border-[#edf2f7]">
                    <td className="py-2 pr-3 text-right tabular-nums text-[#64748b]">{chunk}</td>
                    <td className="py-2 pr-3 font-mono text-[12px] text-[#0f1e38]">{receipt}</td>
                    <td className="py-2 text-right">
                      <Pill tone={status === 'Accepted' ? 'success' : 'info'}>{status}</Pill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="absolute bottom-0 left-0 w-[400px] rounded-xl border border-[#e3edf7] bg-white p-5 shadow-[0_12px_32px_rgba(35,79,146,0.16)]">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-semibold tracking-[0.08em] text-[#64748b] uppercase">E-file</p>
                <p className="mt-1 text-[17px] font-semibold text-[#0f1e38]">1099-NEC, tax year 2025</p>
              </div>
              <Pill tone="warning">Accepted with errors</Pill>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-4 border-t border-[#edf2f7] pt-4">
              {stats.map(([label, value, color]) => (
                <div key={label}>
                  <p className="text-[12px] text-[#64748b]">{label}</p>
                  <p className="mt-0.5 text-[22px] leading-7 font-semibold tabular-nums" style={{ color }}>
                    {value}
                  </p>
                </div>
              ))}
            </div>
            <p className="mt-4 text-[13px] text-[#3b4a63]">
              23 records need a correction. Edit them here and Tab Service files the correction.
            </p>
          </div>
        </div>
      </div>

      <ul className="flex flex-wrap gap-x-6 gap-y-2 text-[13px] text-[#3b4a63]">
        {points.map((item) => (
          <li key={item} className="flex items-center gap-2">
            <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-[#244f92]" />
            {item}
          </li>
        ))}
      </ul>
    </aside>
  );
}
